import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FilterMatchMode } from "primereact/api";
import { TransactionalSubheader } from "iuvity-ui-components/dist/transactionalsubheader";
import { SubheaderActions } from "iuvity-ui-components/dist/subheaderactions";
import { BoxFull } from "iuvity-ui-components/dist/boxfull";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { FormSkeleton, Header } from "../../components";
import {
  CustomDialog,
  DialogProps,
} from "../../components/dialog/CustomDialog";
import {
  CustomDeleteDialog,
  CustomDeleteDialogProps,
} from "../../components/dialog/CustomDeleteDialog";
import { TableHeader } from "../../components/datatable/TableHeader";
import { Actions } from "../../components/datatable/Actions";
import { useTenants } from "../../hooks/useTenants";
import { Tenant } from "../../interfaces";
import { AppContext } from "../../context/index";
import { TenantCreatorSchema } from "../../validations/schemas";
import { Button, TabView } from "iuvity-ui-components";
import { Controller, useForm, useWatch } from "react-hook-form";
import { RemovedTenants } from "../../components/tenants/RemovedTenants";
import { MultiSelect } from "primereact/multiselect";
import { Column } from 'primereact/column'
import { Dropdown } from 'primereact/dropdown'
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { formatStringToDate, formatStringToDate2 } from "../../helpers/formatDate";
import { Tag } from "iuvity-ui-components/dist/tag";
import { capitalizeFirstLetter } from "../../utils";
import { allFieldsBodyTemplate } from "../../components/datatable/allFieldsBodyTemplate";
import { DeleteTenant } from "../../components/tenants/DeleteTenant";
import { tableBasicProps, columnBasicProps } from "../../components/datatable/customTables/basicProps";
import { subheaderActionsPrivilegeValidator } from "../../helpers/subheaderActionsPrivilegeValidator";

const filterDefaultValues: {
  name: string;
  alias: string;
  shortname: string;
  primarycolor: string;
  secondarycolor: string;
  logoname: string;
  isotypelogoname: string;
  status: string;
  guid: string;
  createdat: string;
  tenantcode: string;
  parent: string;
} = {
  name: '',
  alias: '',
  shortname: '',
  primarycolor: '',
  secondarycolor: '',
  logoname: '',
  isotypelogoname: '',
  status: '',
  guid: '',
  createdat: '',
  tenantcode: '',
  parent: ''
};

export const AdminTenantsPage = () => {
  const navigate = useNavigate();
  const {
    t,
    toast,
    appState: { user, toastData, language, selectedLabel,
      pageFilter, selectedTenant, permissiondInfoByRole, selectedTenantId, tenantId },
    setAppState,
  } = useContext(AppContext);

  const tenantParentId = selectedTenantId || tenantId;

  if (toastData) {
    toast.current.replace(toastData)
    setAppState((prev) => ({
      ...prev,
      toastData: null,
    }))
  }

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
        name: pageFilter?.name || '',
        shortname: pageFilter?.shortname || '',
        alias: pageFilter?.alias || '',
        guid: pageFilter?.guid || '',
        parent: pageFilter?.parent || '',
        tenantcode: pageFilter?.tenantcode || '',
        primarycolor: pageFilter?.primarycolor || '',
        secondarycolor: pageFilter?.secondarycolor || '',
        logoname: pageFilter?.logoname || '',
        isotypelogoname: pageFilter?.isotypelogoname || '',
        status: pageFilter?.status || '',
      }
      : filterDefaultValues,
  });


  const { data, tenantAction, reloadFn } = useTenants("DATA");
  const [visibleModalDelete, setVisibleModalDelete] = useState<boolean>(false);

  const { control, setValue, getValues } = form;
  const nameValues = useWatch({
    control,
    name: "name",
  });

  const shortnameValues = useWatch({
    control,
    name: "shortname",
  });

  const statusValues = useWatch({
    control,
    name: "status",
  });

  const primarycolorValues = useWatch({
    control,
    name: "primarycolor",
  });

  const secondarycolorValues = useWatch({
    control,
    name: "secondarycolor",
  });

  const logonameValues = useWatch({
    control,
    name: "logoname",
  });

  const isotypelogonameValues = useWatch({
    control,
    name: "isotypelogoname",
  });

  const aliasValues = useWatch({
    control,
    name: "alias",
  });

  const guidValues = useWatch({
    control,
    name: "guid",
  });

  const parentValues = useWatch({
    control,
    name: "parent",
  });

  const tenantcodeValues = useWatch({
    control,
    name: "tenantcode",
  });

  const createdatValues = useWatch({
    control,
    name: "createdat",
  });

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("Tenants.nameLabel"),
        },
        {
          field: "alias",
          header: t("Tenants.aliasLabel"),
        },
        {
          field: "guid",
          header: t("Tenants.guidLabel"),
        },
        {
          field: "parent",
          header: t("Tenants.parentLabel"),
        },
        {
          field: "status",
          header: t("Tenants.statusLabel"),
        },
        {
          field: "tenantcode",
          header: t("Tenants.tenantcodeLabel"),
        },
        {
          field: "primarycolor",
          header: t("Tenants.primarycolorLabel"),
        },
        {
          field: "secondarycolor",
          header: t("Tenants.secondarycolorLabel"),
        },
        {
          field: "logoname",
          header: t("Tenants.logonameLabel"),
        },
        {
          field: "isotypelogoname",
          header: t("Tenants.isotypelogonameLabel"),
        },
        {
          field: "createdat",
          header: t("Tenants.createdatLabel"),
        },
        {
          field: "actions",
          header: t("Tenants.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const [modalProps, setModalProps] = useState<{
    visible: boolean;
    type: "CREATE" | "VIEW" | "EDIT" | null;
  }>({
    visible: false,
    type: null,
  });

  const [auxModal, auxModalController] = useState<DialogProps>({
    visible: false,
  } as DialogProps);

  const [auxDeleteModal, auxDeleteModalController] =
    useState<CustomDeleteDialogProps>({
      visible: false,
    } as CustomDeleteDialogProps);

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [selectedColumns, setSelectedColumns] = useState(columns);
  const defaultFilterValues = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.IN },
    shortname: { value: null, matchMode: FilterMatchMode.IN },
    alias: { value: null, matchMode: FilterMatchMode.IN },
    primarycolor: { value: null, matchMode: FilterMatchMode.IN },
    secondarycolor: { value: null, matchMode: FilterMatchMode.IN },
    logoname: { value: null, matchMode: FilterMatchMode.IN },
    isotypelogoname: { value: null, matchMode: FilterMatchMode.IN },
    status: { value: null, matchMode: FilterMatchMode.EQUALS },
    tenantcode: { value: null, matchMode: FilterMatchMode.IN },
    guid: { value: null, matchMode: FilterMatchMode.IN },
    parent: { value: null, matchMode: FilterMatchMode.IN },
    createdat: { value: null, matchMode: FilterMatchMode.IN },
  };
  const [filters, setFilters] = useState(defaultFilterValues);
  const [filteredData, setFilteredData] = useState<any | null>(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const onAction = (type: "CREATE" | "VIEW" | "EDIT", tenant?: any) => {
    if (type === "VIEW" && tenant) {
      setAppState((prev) => ({
        ...prev,
        selectedTenant: tenant,
        tenants: data,
        edit: false
      }));
      navigate("/tenants/view");
    }
    if (type === 'EDIT') {
      setAppState((prev) => ({
        ...prev,
        selectedTenant: tenant,
        tenants: data,
        edit: true
      }));
      navigate("/tenants/view");
    }

    setModalProps((prev) => ({
      ...prev,
      visible: true,
      type,
    }));
  };

  const onDeleteAction = async (tenant: Tenant) => {
    await tenantAction("DELETE", tenant.id);
    reloadFn();
  };

  const handleModalProps = async (
    type: string,
    tenant?: Tenant | null
  ) => {
    switch (type) {
      case "DELETE":
        await onDeleteAction(tenant!);
        setAppState((prev) => ({
          ...prev,
          selectedTenant: null,
        }));
        setVisibleModalDelete(false);
        return;
      default:
        return;
    }
  };

  useEffect(() => {
    if (language)
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(`Tenants.${col.field}Label`),
        }))
      );
  }, [t, language, setSelectedColumns]);

  const onClearFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.IN },
      shortname: { value: null, matchMode: FilterMatchMode.IN },
      alias: { value: null, matchMode: FilterMatchMode.IN },
      primarycolor: { value: null, matchMode: FilterMatchMode.IN },
      secondarycolor: { value: null, matchMode: FilterMatchMode.IN },
      logoname: { value: null, matchMode: FilterMatchMode.IN },
      isotypelogoname: { value: null, matchMode: FilterMatchMode.IN },
      status: { value: null, matchMode: FilterMatchMode.EQUALS },
      tenantcode: { value: null, matchMode: FilterMatchMode.IN },
      guid: { value: null, matchMode: FilterMatchMode.IN },
      parent: { value: null, matchMode: FilterMatchMode.IN },
      createdat: { value: null, matchMode: FilterMatchMode.IN },
    })
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  const [disableClearFilterButton, setDisableClearFilterButton] = useState<{
    name: boolean;
    alias: boolean;
    shortname: boolean;
    primarycolor: boolean;
    secondarycolor: boolean;
    logoname: boolean;
    isotypelogoname: boolean;
    status: boolean;
    guid: boolean;
    createdat: boolean;
    tenantcode: boolean;
    parent: boolean;
  }>({
    name: false,
    alias: false,
    shortname: false,
    primarycolor: false,
    secondarycolor: false,
    logoname: false,
    isotypelogoname: false,
    status: false,
    guid: false,
    createdat: false,
    tenantcode: false,
    parent: false
  });
  const onClearFiltersPerTable = (options: any, filtersFromSession: any) => {
    setFilters({
      global: { value: options?.field === 'global' ? null : filtersFromSession?.global?.value, matchMode: FilterMatchMode.CONTAINS },
      name: { value: options?.field === 'name' ? null : filtersFromSession?.name?.value, matchMode: FilterMatchMode.IN },
      shortname: { value: options?.field === 'shortname' ? null : filtersFromSession?.shortname?.value, matchMode: FilterMatchMode.IN },
      alias: { value: options?.field === 'alias' ? null : filtersFromSession?.alias?.value, matchMode: FilterMatchMode.IN },
      primarycolor: { value: options?.field === 'primarycolor' ? null : filtersFromSession?.primarycolor?.value, matchMode: FilterMatchMode.IN },
      secondarycolor: { value: options?.field === 'secondarycolor' ? null : filtersFromSession?.secondarycolor?.value, matchMode: FilterMatchMode.IN },
      logoname: { value: options?.field === 'logoname' ? null : filtersFromSession?.logoname?.value, matchMode: FilterMatchMode.IN },
      isotypelogoname: { value: options?.field === 'isotypelogoname' ? null : filtersFromSession?.isotypelogoname?.value, matchMode: FilterMatchMode.IN },
      status: { value: options?.field === 'status' ? null : filtersFromSession?.status?.value, matchMode: FilterMatchMode.EQUALS },
      tenantcode: { value: options?.field === 'tenantcode' ? null : filtersFromSession?.tenantcode?.value, matchMode: FilterMatchMode.IN },
      guid: { value: options?.field === 'guid' ? null : filtersFromSession?.guid?.value, matchMode: FilterMatchMode.IN },
      parent: { value: options?.field === 'parent' ? null : filtersFromSession?.parent?.value, matchMode: FilterMatchMode.IN },
      createdat: { value: options?.field === 'createdat' ? null : filtersFromSession?.createdat?.value, matchMode: FilterMatchMode.IN },
    });

    // luego de dar click en limpiar, elimina los criterios de búsqueda.
    setValue(options?.field, '');
    setDisableClearFilterButton((prev: any) => ({
      ...prev,
      [options?.field]: true,
    }));
    setAppState((prev) => ({
      ...prev,
      pageFilter: {
        ...prev.pageFilter,
        [options.field]: '',
      }
    }));
  };

  useEffect(() => {
    if (data && !pageFilter) {
      setFilteredData(data);
    } else if (!data) {
      setFilteredData(null);
    }
  }, [data, setFilteredData, setAppState, selectedLabel, pageFilter]);

  // Esta implementación es para setear la data de la tabla si se aplican filtros.
  useLayoutEffect(() => {
    if (data && pageFilter) {
      setFilteredData(
        data.filter(
          (item: any) => {
            return ((form.getValues("name")?.length > 0
              ? form.getValues("name").includes(item.name)
              : true) &&
              (form.getValues("shortname")?.length > 0
                ? form.getValues("shortname").includes(item.shortname)
                : true) &&
              (form.getValues("primarycolor")?.length > 0
                ? form.getValues("primarycolor").includes(item.tenantbranding.primarycolor)
                : true) &&
              (form.getValues("secondarycolor")?.length > 0
                ? form.getValues("secondarycolor").includes(item.tenantbranding.secondarycolor)
                : true) &&
              (form.getValues("logoname")?.length > 0
                ? form.getValues("logoname").includes(item.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0].name)
                : true) &&
              (form.getValues("isotypelogoname")?.length > 0
                ? form.getValues("isotypelogoname").includes(item.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0].name)
                : true) &&
              (form.getValues("status") !== ''
                ? t(`Tenants.${item?.status?.toLowerCase()}StatusLabel`) === form.getValues("status") : true))
          }
        )
      );
    } else if (data) {
      setFilteredData(data);
    }
  }, [data, setFilteredData, form, pageFilter]);

  const statusBodyTemplate = (rowData: any) => (
    <Tag
      value={capitalizeFirstLetter(rowData.status)}
      severity={
        (['active', 'activo', 'activa'].includes(rowData.status.toLowerCase()) && 'success') ||
        (['draft', 'borrador'].includes(rowData.status.toLowerCase()) && 'info') ||
        (['delete', 'eliminado'].includes(rowData.status.toLowerCase()) && 'danger') ||
        'warning'
      }
    />
  );
  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  }
  const filterTemplate = (options: any) => {
    setTimeout(() => {
      const emptyChipList = document.getElementsByClassName('p-multiselect-token-label');
      [...emptyChipList].map((chip) => {
        if (chip.innerHTML.toString() === '') {
          chip.parentElement!.style.display = 'none';
        };
      });
    }, 100);
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('filters-table') ?? '{}')?.filters;
    const arrayFiltersNotNull = !!filtersFromSession && Object.keys(filtersFromSession)?.filter((field: string) => !!filtersFromSession[field]?.value);
    let arrayFilterResult = data;
    if (!!arrayFiltersNotNull && arrayFiltersNotNull.length > 0) {
      for (let i = 0; i < arrayFiltersNotNull.length; i++) {
        arrayFilterResult = arrayFilterResult?.filter((element: any) => filtersFromSession[arrayFiltersNotNull[i]]?.value?.includes((() => {
          return (arrayFiltersNotNull[i] === 'logoname')
            ? element?.images.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name
            : (arrayFiltersNotNull[i] === 'isotypelogoname'
              ? element?.images.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name
              : (((arrayFiltersNotNull[i] === 'primarycolor' || arrayFiltersNotNull[i] === 'secondarycolor')
                ? element?.tenantbranding[arrayFiltersNotNull[i]]
                : (arrayFiltersNotNull[i] === 'status'
                  ? t(`Tenants.${element?.[arrayFiltersNotNull[i]].toLowerCase()}StatusLabel`)
                  : (arrayFiltersNotNull[i] === 'parent'
                    ? element[arrayFiltersNotNull[i]]?.name
                    : element[arrayFiltersNotNull[i]])))));
        })()));
      };
    }

    const arrayOriginal = arrayFilterResult
      ?.filter((tenant: any) => tenant.status !== 'DELETED')
      ?.map((tenant: any) =>
        ['primarycolor', 'secondarycolor'].includes(options.field)
          ? ({
            label: tenant?.tenantbranding ? tenant?.tenantbranding[`${options.field}`] : false,
            value: tenant?.tenantbranding ? tenant?.tenantbranding[`${options.field}`] : false
          })
          : ['logoname'].includes(options.field)
            ? ({
              label: tenant?.images.length > 0 ? tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0].name : false,
              value: tenant?.images.length > 0 ? tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0].name : false
            })
            : ['isotypelogoname'].includes(options.field)
              ? (({
                label: tenant?.images.length > 0 ? tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0].name : false,
                value: tenant?.images.length > 0 ? tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0].name : false
              }))
              : ['parent'].includes(options.field)
                ? (({
                  label: !!tenant?.parent ? tenant?.parent?.name : false,
                  value: !!tenant?.parent ? tenant?.parent?.name : false
                }))
                : ['createdat'].includes(options.field)
                  ? (({
                    label: formatStringToDate(tenant?.createdat, true, t),
                    value: formatStringToDate(tenant?.createdat, true, t)
                  }))
                  : ['status'].includes(options.field)
                    ? (({
                      value: t(`Tenants.${tenant?.status?.toLowerCase()}StatusLabel`),
                      label: t(`Tenants.${tenant?.status?.toLowerCase()}StatusLabel`),
                    }))
                    : ({ label: tenant[`${options.field}`], value: tenant[`${options.field}`] }))
      ?.filter((item: { label: string, value: string }) => item.label)
      ?.sort((a: any, b: any) => a.label.localeCompare(b.label));

    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) =>
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      options.field === 'status'
        ? statusFilterTemplate(options)
        : <Controller
          name={options.field}
          control={control}
          // rules={TenantCreatorSchema(t).name}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Tenants.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  optionLabel={'label'}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Tenants.${options.field}FilterPlaceholder`)}
                  className="mb-2"
                  emptyFilterMessage={t("Tenants.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
        />
    )
  }

  const statuses = [
    `${t("Tenants.activeStatusLabel")}`,
    `${t("Tenants.inactiveStatusLabel")}`,
    `${t("Tenants.draftStatusLabel")}`,
  ];
  const statusItemTemplate = (option: any) => {
    return <span>{option}</span>;
  }
  const statusFilterTemplate = (options: any) => {
    return (
      <Controller
        name={options.field}
        control={control}
        render={({ field }) => {
          field.onChange = (e) => {
            setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
            options.filterCallback(e.value);
          };
          return (
            <>
              <label htmlFor={options.field} className="block lb mt-2 mb-3">
                {t(`Tenants.${options.field}Label`)}
              </label>
              {/* // @ts-ignore */}
              <Dropdown
                options={statuses}
                itemTemplate={statusItemTemplate}
                placeholder={t(`Tenants.${options.field}FilterPlaceholder`)}
                className="p-column-filter mb-2"
                showClear={false}
                {...field}
              />
            </>
          );
        }}
      />

    )
  }

  const filterClearTemplate = (options: any) => {
    const filtersFromSession = JSON.parse(sessionStorage?.getItem('filters-table') ?? '{}')?.filters;

    return (
      <Button
        label={t("Tenants.clearFilterLabel")}
        variant="p-button-text"
        disabled={(filtersFromSession?.hasOwnProperty('global')
          ? !filtersFromSession[options?.field]?.value : true)
          // @ts-ignore
          || disableClearFilterButton[options?.field]}
        onClick={() => onClearFiltersPerTable(options, filtersFromSession)}
      />
    )
  }

  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        itemID={options?.field}
        label={t("Tenants.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            (options?.field === 'name' && nameValues?.length > 0) ||
            (options?.field === 'shortname' && shortnameValues?.length > 0) ||
            (options?.field === 'guid' && guidValues?.length > 0) ||
            (options?.field === 'parent' && parentValues?.length > 0) ||
            (options?.field === 'tenantcode' && tenantcodeValues?.length > 0) ||
            (options?.field === 'primarycolor' && primarycolorValues?.length > 0) ||
            (options?.field === 'secondarycolor' && secondarycolorValues?.length > 0) ||
            (options?.field === 'logoname' && logonameValues?.length > 0) ||
            (options?.field === 'isotypelogoname' && isotypelogonameValues?.length > 0) ||
            (options?.field === 'alias' && aliasValues?.length > 0) ||
            // (options?.field === 'guid' && guidValues??.length > 0) ||
            // (options?.field === 'parent' && parentValues??.length > 0) ||
            (options?.field === 'createdat' && createdatValues?.length > 0) ||
            // (options?.field === 'tenantcode' && tenantcodeValues??.length > 0) ||
            (options?.field === 'status' && statusValues !== '')
          )
        }
        onClick={() => {
          options.filterApplyCallback();
          setAppState((prev) => ({
            ...prev,
            pageFilter: { ...getValues() },
          }));
          setDisableClearFilterButton((prev: any) => ({
            ...prev,
            [options?.field]: false,
          }));
        }}
      />
    )
  };

  const allFieldsBodyTemplate_Tenants = (rowData: any, field: string, criteria: string) => {
    return (
      <div
        style={{
          boxShadow: (!rowData[criteria] && field !== 'actions')
            ? 'var(--backgroundColor) 0px 130px 0px 150px'
            : 'none',
          background: (!rowData[criteria] && field !== 'actions')
            ? 'var(--backgroundColor)'
            : 'none',
          width: '300px',
        }}
      >
        {rowData[field]}
      </div>)
  };

  useEffect(() => {
    if (sessionStorage.getItem('filters-table')) {
      const dataSession = JSON.parse(sessionStorage.getItem('filters-table') || "");
      const global = dataSession.filters.global;
      setGlobalFilterValue(global);
    }
  }, [filters]);

  return (
    <>
      {/* <Header
        welcomeMessage={t("Dashboard.welcomeTitle")}
        userName={user?.name}
      /> */}
      <TransactionalSubheader
        subheaderTitle={
          <div>
            {t("Tenants.tenantAdminTitle")}
          </div>
        }
      >
        <TransactionalSubheader.BreadCrumb
          id={t("Tenants.tenantsBreadcrumbLabel")}
          disabledDefaultHome={true}
          breadcrumbsHomeItem={{
            icon: "iu iu-icon-home",
            command: () => navigate("/"),
          }}
          breadcrumbsItems={[
            {
              label: t("Tenants.advancedOptionsBreadcrumbLabel"),
              style: { fontWeight: "bold" },
              disabled: true
            },
            {
              label: t("Tenants.tenantsBreadcrumbLabel"),
              disabled: true
            },
          ]}
        />
        <TransactionalSubheader.Title />
        <div className="t-subheader-content">
          <span>{t("Tenants.tenantAdminDescription1")}</span>
        </div>
      </TransactionalSubheader>

      <BoxFull className="my-4 px-0 py-0" variant="tertiary">
        <TabView
          className="upload-tabview mt-4"
          activeIndex={activeIndex}
          onTabChange={(e) => {
            setGlobalFilterValue("");
            sessionStorage.removeItem('filters-table');
            setFilters(defaultFilterValues);
            setActiveIndex(e.index);
            if (e.index === 0 && form.formState.isDirty) form.reset();
          }}
          items={[
            {
              key: "1",
              header: t("Tenants.availableTenants"),
              children: (
                <>
                  <BoxFull className="ml-0 mt-0 mx-0 px-0" variant="tertiary">
                    <div className="subheader-buttons">
                      <SubheaderActions
                        className="px-0 pt-0 pb-0"
                        actions={subheaderActionsPrivilegeValidator([
                          {
                            id: "111",
                            icon: "iu iu-icon-plus",
                            label: t("Tenants.newTenantTitle"),
                            style: {
                              display: !permissiondInfoByRole.CREATE ? 'none' : undefined,
                            },
                            onClick: () => {
                              setAppState((prev) => ({
                                ...prev,
                                tenants: data
                              }));
                              navigate('/tenants/new')
                            },
                          },
                        ])}
                      />
                    </div>
                  </BoxFull>
                  <BoxFull className="dt-c my-0 mx-0">
                    {data ? (
                      <DataTable
                        header={
                          <TableHeader
                            title={t("Tenants.tableAvailableTenants")}
                            columns={columns}
                            selectedColumns={selectedColumns}
                            toggleColumns={setSelectedColumns}
                            filters={filters}
                            setFilters={setFilters}
                            reload={reloadFn}
                            onClearFiltersColumn={onClearFilters}
                            globalFilterValue={globalFilterValue}
                          />
                        }
                        filters={filters}
                        //sortField="id"
                        dataKey="id"
                        //sortOrder={1}
                        value={filteredData?.filter((tenant: any) => tenant?.status !== 'DELETED')
                          .sort((tenantA: any, tenantB: any) => new Date(tenantB.createdat).getTime() - new Date(tenantA.createdat).getTime())
                          .sort((tenantA: any, tenantB: any) => {
                            if (tenantA.id === tenantParentId) return -1;
                            return 0;
                          })
                          .map((tenant: any) => ({
                            ...tenant,
                            status: t(`Tenants.${tenant.status.toLowerCase()}StatusLabel`),
                            primarycolor: tenant?.tenantbranding?.primarycolor,
                            secondarycolor: tenant?.tenantbranding?.secondarycolor,
                            parent: tenant?.parent?.name || tenant.name,
                            createdat: !!tenant?.createdat ? formatStringToDate2(tenant?.createdat, true, t) : null,
                            logoname: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_LOGO")[0]?.name,
                            isotypelogoname: tenant?.images?.filter((item: any) => item.entity === "TENANT_BRANDING_ISOTYPE_LOGO")[0]?.name,
                            actions: (
                              <Actions
                                enableDelete={true}
                                onDetailAndEdit={() => onAction("VIEW", tenant)}
                                viewButtonEdit={true}
                                onEdit={() => onAction('EDIT', tenant)}
                                onDelete={() => {
                                  setAppState((prev) => ({
                                    ...prev,
                                    selectedTenant: tenant,
                                  }));
                                  setVisibleModalDelete(true);
                                }
                                }
                                visibleObject={{ ...permissiondInfoByRole }}
                              />
                            ),
                          }))}
                        globalFilterFields={['name', 'alias', 'shortname', 'primarycolor', 'secondarycolor', 'logoname', 'isotypelogoname', 'status', 'guid', 'parent', 'createdat', 'tenantcode']}
                        filterDisplay="menu"
                        {...tableBasicProps(
                          t,
                          {
                            fieldBackgroundDynamic: '',
                            criteriaBackgroundDynamic: '',
                          },
                          {
                            hasEmptyTemplate:true,
                            idEmptyTemplate: 'admin-tenants-empty-box'
                          }
                        )}
                      >
                        {selectedColumns.map((col) => {
                          return (
                            <Column
                              key={col.field}
                              field={col.field}
                              header={col.header}
                              sortable={!(col.field === "actions")}
                              align={col.field === "actions" ? "center" : "left"}
                              frozen={col.field === "actions" ? true : false}
                              alignFrozen={col.field === "actions" ? "right" : undefined}
                              filterField={col.field}
                              showFilterMatchModes={false}
                              filterMenuStyle={{ width: '23rem' }}
                              filter={!['actions'].includes(col.field)}
                              filterElement={(options) => filterTemplate(options)}
                              // ((
                              //   col.field === "name" ||
                              //   col.field === "alias" ||
                              //   col.field === "guid" ||
                              //   col.field === "parent" ||
                              //   col.field === "primarycolor" ||
                              //   col.field === "secondarycolor" ||
                              //   col.field === "logoname" ||
                              //   col.field === "isotypelogoname" ||
                              //   col.field === "createdat" ||
                              //   col.field === "tenantcode"
                              // ) && filterTemplate) ||
                              // (col.field === "status" && statusFilterTemplate)
                              // }
                              filterApply={filterApplyTemplate}
                              filterClear={filterClearTemplate}
                              body={(rowData) => col.field === "status"
                                ? statusBodyTemplate(rowData)
                                : <div>{rowData[col.field]}</div>}
                              {...columnBasicProps({ col })}
                            />
                          );
                        })}

                      </DataTable>
                    ) : (
                      <FormSkeleton length={8} />
                    )}
                  </BoxFull>
                </>
              ),
              disabled: false,
            },
            {
              key: "2",
              header: t("Tenants.removedTenants"),
              children: (
                <RemovedTenants
                  title={t("Tenants.tableRemovedTenants")}
                  data={data}
                  actionFn={tenantAction}
                  reloadFn={reloadFn}
                />
              ),
            },
          ]}
        />
      </BoxFull>

      <CustomDialog
        title={auxModal.title}
        visible={auxModal.visible}
        content={auxModal.content}
        handleModalState={auxModalController}
        onConfirm={auxModal.onConfirm}
      />
      <DeleteTenant
        visible={visibleModalDelete}
        closeHandler={() => { setVisibleModalDelete(false) }}
        confirmHandler={() => handleModalProps("DELETE", selectedTenant)}
      />
    </>
  );
};
